// app/course-preview/[id].tsx
// דף פרוקסי פנימי — מוצג לפני מעבר לאתר הקורס החיצוני
// ✅ Fixes:
// 1) "קורסים נוספים" — אם לא הגיע ב-params -> מביא המלצות מה-DB (ולא מציג הודעת "לא התקבלו...")
// 2) אנימציות כניסה (Fade + Slide, כולל Stagger לקורסים נוספים)
// 3) כותרת Header RTL (headerTitle כ-Text עם writingDirection)

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  Platform,
  ActivityIndicator,
  Alert,
  Linking,
  Animated,
} from 'react-native';
import Head from 'expo-router/head';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { ArrowRight, ExternalLink, Phone, Clock, BookOpen, Users, Award } from 'lucide-react-native';
import { supabase } from '@/utils/supabase';
import { theme } from '@/constants/theme';
import { BookCallModal } from '@/components/BookCallModal';
import { Course } from '@/types/chat';

const isWeb = Platform.OS === 'web';

function safeStr(v: any): string {
  return (v ?? '').toString().trim();
}

function getFirstCsv(raw?: string | null): string {
  const s = safeStr(raw);
  if (!s) return '';
  return s.split(',').map((p) => p.trim()).filter(Boolean)[0] || '';
}

function normalizeUrl(raw?: string | null): string {
  const s = safeStr(raw);
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[\w-]+\.[\w.-]+/i.test(s)) return `https://${s}`;
  return s;
}

function safeJsonParse<T>(raw: any, fallback: T): T {
  try {
    const s = safeStr(raw);
    if (!s) return fallback;
    return JSON.parse(s) as T;
  } catch {
    return fallback;
  }
}

type CourseRow = {
  id: number;
  course_id: string;
  source_url_key: string;
  title: string;
  provider: string;
  topic: string;
  type: string;
  level: string;
  format: string;
  price: string;
  duration: string;
  language: string;
  summary: string;
  source_urls: string;
  voucher_text: string;
  has_voucher: boolean;
  audience: string;
  outcomes: string;
  deliverables: string;
};

type RelatedMini = {
  course_id: string;
  title: string;
  provider: string;
  topic?: string;
  type?: string;
  level?: string;
  format?: string;
  price?: string;
  duration?: string;
  has_voucher?: boolean;
  voucher_text?: string;
};

function toMini(c: any): RelatedMini {
  return {
    course_id: safeStr(c.course_id),
    title: safeStr(c.title),
    provider: safeStr(c.provider),
    topic: safeStr(c.topic),
    type: safeStr(c.type),
    level: safeStr(c.level),
    format: safeStr(c.format),
    price: safeStr(c.price),
    duration: safeStr(c.duration),
    has_voucher: !!c.has_voucher,
    voucher_text: safeStr(c.voucher_text),
  };
}

export default function CoursePreviewPage() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const courseKey = safeStr(params.id);

  const [course, setCourse] = useState<CourseRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [bookCallVisible, setBookCallVisible] = useState(false);

  // אנימציות כניסה
  const enter = useRef(new Animated.Value(0)).current;

  // קורסים נוספים
  const relatedFromParams = useMemo(() => {
    // תומך בכמה שמות אפשריים כדי להיות חסין:
    // related / related_courses / chat_courses
    const raw =
      (params.related as any) ??
      (params.related_courses as any) ??
      (params.chat_courses as any);
    const arr = safeJsonParse<any[]>(raw, []);
    const cleaned = (Array.isArray(arr) ? arr : [])
      .map(toMini)
      .filter((x) => x.course_id && x.title)
      .slice(0, 8);
    return cleaned;
  }, [params.related, params.related_courses, params.chat_courses]);

  const [related, setRelated] = useState<RelatedMini[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);

  useEffect(() => {
    // header RTL
    // NOTE: Stack.Screen חייב להיות בתוך קומפוננטה (ולא בקובץ layout)
  }, []);

  useEffect(() => {
    let alive = true;

    const fetchCourse = async () => {
      try {
        setLoading(true);
        if (!courseKey) {
          if (alive) setCourse(null);
          return;
        }

        const { data, error } = await supabase
          .from('processed_raw_courses')
          .select(
            'id,course_id,source_url_key,title,provider,topic,type,level,format,price,duration,language,summary,source_urls,voucher_text,has_voucher,audience,outcomes,deliverables'
          )
          .is('deleted_at', null)
          .or(`course_id.eq.${courseKey},source_url_key.eq.${courseKey}`)
          .order('id', { ascending: false })
          .limit(1);

        if (!alive) return;

        if (error || !data || data.length === 0) {
          setCourse(null);
          return;
        }

        const c = data[0] as any;
        const normalized: CourseRow = {
          ...c,
          course_id: safeStr(c.course_id),
          source_url_key: safeStr(c.source_url_key),
          title: safeStr(c.title),
          provider: safeStr(c.provider),
          topic: safeStr(c.topic),
          type: safeStr(c.type),
          level: safeStr(c.level),
          format: safeStr(c.format),
          price: safeStr(c.price),
          duration: safeStr(c.duration),
          language: safeStr(c.language),
          summary: safeStr(c.summary),
          source_urls: safeStr(c.source_urls),
          voucher_text: safeStr(c.voucher_text),
          audience: safeStr(c.audience),
          outcomes: safeStr(c.outcomes),
          deliverables: safeStr(c.deliverables),
          has_voucher: !!c.has_voucher,
        };

        setCourse(normalized);
      } catch {
        if (alive) setCourse(null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchCourse();
    return () => {
      alive = false;
    };
  }, [courseKey]);

  // אנימציית כניסה (רק אחרי שהעמוד יודע מה המצב)
  useEffect(() => {
    enter.setValue(0);
    Animated.timing(enter, {
      toValue: 1,
      duration: 420,
      useNativeDriver: true,
    }).start();
  }, [enter, loading, !!course]);

  // קורסים נוספים:
  // - אם הגיעו ב-params -> מציג
  // - אם לא הגיעו -> מביא מה-DB לפי topic (ואז provider כ-fallback)
  useEffect(() => {
    let alive = true;

    const fetchRelated = async () => {
      try {
        // אם עדיין טוענים/אין קורס — אל תביא
        if (loading || !course) return;

        // אם הגיעו מהצ'אט/params — מציגים אותם, בלי DB
        if (relatedFromParams.length > 0) {
          if (alive) setRelated(relatedFromParams.filter((r) => r.course_id !== course.course_id));
          return;
        }

        setRelatedLoading(true);

        const baseSelect =
          'course_id,title,provider,topic,type,level,format,price,duration,has_voucher,voucher_text';

        // 1) נסה לפי topic
        if (course.topic) {
          const { data } = await supabase
            .from('processed_raw_courses')
            .select(baseSelect)
            .is('deleted_at', null)
            .neq('course_id', course.course_id)
            .eq('topic', course.topic)
            .order('id', { ascending: false })
            .limit(6);

          const list = (data || []).map(toMini).filter((x) => x.course_id && x.title);
          if (!alive) return;

          if (list.length > 0) {
            setRelated(list);
            return;
          }
        }

        // 2) fallback לפי provider
        if (course.provider) {
          const { data } = await supabase
            .from('processed_raw_courses')
            .select(baseSelect)
            .is('deleted_at', null)
            .neq('course_id', course.course_id)
            .eq('provider', course.provider)
            .order('id', { ascending: false })
            .limit(6);

          const list = (data || []).map(toMini).filter((x) => x.course_id && x.title);
          if (!alive) return;

          setRelated(list);
          return;
        }

        // 3) אם אין גם topic וגם provider — לא מציגים כלום
        if (alive) setRelated([]);
      } catch {
        if (alive) setRelated([]);
      } finally {
        if (alive) setRelatedLoading(false);
      }
    };

    fetchRelated();
    return () => {
      alive = false;
    };
  }, [loading, course?.course_id, course?.topic, course?.provider, relatedFromParams]);

  const openExternal = useCallback(async () => {
    const raw = course ? getFirstCsv(course.source_urls) : '';
    const url = normalizeUrl(raw);
    if (!url) {
      if (!isWeb) Alert.alert('אין קישור', 'לקורס זה אין קישור חיצוני כרגע.');
      return;
    }
    if (isWeb && typeof window !== 'undefined') {
      (window as any).open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const ok = await Linking.canOpenURL(url);
      if (!ok) {
        Alert.alert('לא ניתן לפתוח קישור', 'הלינק לא תקין במכשיר זה.');
        return;
      }
      await Linking.openURL(url);
    } catch {
      Alert.alert('שגיאה', 'נכשלנו בפתיחת הקישור. נסה שוב.');
    }
  }, [course]);

  const goToRelated = useCallback(
    (id: string) => {
      const target = safeStr(id);
      if (!target) return;
      router.push({
        pathname: '/course-preview/[id]' as any,
        params: { id: target },
      } as any);
    },
    [router]
  );

  // בנה אובייקט Course עבור BookCallModal מתוך נתוני DB
  const courseForModal: Course | null = course
    ? {
        id: course.course_id,
        name: course.title,
        description: course.summary,
        mentor: course.provider,
        price: course.price,
        learningType: course.format,
        duration: course.duration,
        audience: course.audience,
        outcomes: course.outcomes,
        deliverables: course.deliverables,
      }
    : null;

  const hasExternalUrl = !!(course && normalizeUrl(getFirstCsv(course.source_urls)));

  const animWrapStyle = useMemo(
    () => ({
      opacity: enter,
      transform: [
        {
          translateY: enter.interpolate({
            inputRange: [0, 1],
            outputRange: [10, 0],
          }),
        },
      ],
    }),
    [enter]
  );

  return (
    <View style={styles.page}>
      {isWeb && (
        <Head>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href="https://kursiaii.com/course-preview" />
        </Head>
      )}

      <Stack.Screen
        options={{
          title: 'פרטי קורס',
          headerTitle: () => (
            <Text style={styles.headerTitle} numberOfLines={1}>
              פרטי קורס
            </Text>
          ),
          headerTitleAlign: 'center',
        }}
      />

      <ScrollView contentContainerStyle={styles.wrap} showsVerticalScrollIndicator={false}>
        {/* ← Back */}
        <Pressable
          onPress={() => router.back()}
          style={({ pressed }) => [styles.backBtn, pressed && styles.pressed]}
          accessible
          accessibilityRole="button"
          accessibilityLabel="חזרה"
        >
          <ArrowRight size={18} color={theme.colors.text} />
          <Text style={styles.backText}>חזרה</Text>
        </Pressable>

        {loading ? (
          <View style={styles.center}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text style={styles.muted}>טוען פרטי קורס…</Text>
          </View>
        ) : !course ? (
          <Animated.View style={[styles.card, animWrapStyle]}>
            <Text style={styles.h1}>הקורס לא נמצא</Text>
            <Text style={styles.muted}>יכול להיות שהלינק ישן או שהקורס הוסר.</Text>
            <Pressable
              onPress={() => router.back()}
              style={({ pressed }) => [styles.primaryBtn, pressed && styles.pressed]}
            >
              <Text style={styles.primaryBtnText}>חזרה</Text>
            </Pressable>
          </Animated.View>
        ) : (
          <Animated.View style={animWrapStyle}>
            {/* כרטיס ראשי */}
            <View style={styles.card}>
              {course.has_voucher && !!course.voucher_text && (
                <View style={styles.badge}>
                  <Text style={styles.badgeText}>{course.voucher_text}</Text>
                </View>
              )}

              <Text style={styles.h1}>{course.title}</Text>

              {!!course.provider && <Text style={styles.provider}>{course.provider}</Text>}

              {/* תגיות מטא */}
              <View style={styles.metaRow}>
                {!!course.topic && <Pill text={course.topic} />}
                {!!course.type && <Pill text={course.type} />}
                {!!course.level && <Pill text={course.level} />}
                {!!course.format && <Pill text={course.format} />}
                {!!course.duration && (
                  <Pill icon={<Clock size={11} color={theme.colors.textLight} />} text={course.duration} />
                )}
                {!!course.price && <Pill text={`₪ ${course.price}`} />}
              </View>

              {/* תקציר */}
              {!!course.summary && <Text style={styles.paragraph}>{course.summary}</Text>}
            </View>

            {/* פרטים נוספים */}
            {(!!course.audience || !!course.outcomes || !!course.deliverables) && (
              <View style={styles.card}>
                {!!course.audience && (
                  <InfoSection
                    icon={<Users size={15} color={theme.colors.primary} />}
                    title="קהל יעד"
                    text={course.audience}
                  />
                )}
                {!!course.outcomes && (
                  <InfoSection
                    icon={<Award size={15} color={theme.colors.primary} />}
                    title="מה תצא עם"
                    text={course.outcomes}
                  />
                )}
                {!!course.deliverables && (
                  <InfoSection
                    icon={<BookOpen size={15} color={theme.colors.primary} />}
                    title="מה תקבל"
                    text={course.deliverables}
                  />
                )}
              </View>
            )}

            {/* כפתורי פעולה */}
            <View style={styles.actionsCard}>
              {/* כפתור ראשי — לכידת ליד */}
              <Pressable
                onPress={() => setBookCallVisible(true)}
                style={({ pressed }) => [styles.primaryBtn, pressed && styles.pressed]}
                accessible
                accessibilityRole="button"
                accessibilityLabel="השאר פרטים למכללה"
              >
                <Phone size={17} color="#fff" />
                <Text style={styles.primaryBtnText}>השאר פרטים למכללה</Text>
              </Pressable>

              {/* כפתור משני — מעבר לאתר */}
              {hasExternalUrl && (
                <Pressable
                  onPress={openExternal}
                  style={({ pressed }) => [styles.outlineBtn, pressed && styles.pressed]}
                  accessible
                  accessibilityRole="button"
                  accessibilityLabel="מעבר לאתר הרשמי"
                >
                  <ExternalLink size={15} color={theme.colors.primary} />
                  <Text style={styles.outlineBtnText}>מעבר לאתר הרשמי</Text>
                </Pressable>
              )}
            </View>

            {/* קורסים נוספים */}
            <View style={styles.card}>
              <View style={styles.sectionTitleRow}>
                <Text style={styles.sectionTitle}>קורסים נוספים</Text>
                {relatedFromParams.length > 0 && <Text style={styles.sectionHint}>מהצ׳אט</Text>}
              </View>

              {relatedLoading ? (
                <View style={styles.relatedLoadingRow}>
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                  <Text style={styles.muted}>מחפש עוד קורסים דומים…</Text>
                </View>
              ) : related.length === 0 ? (
                // ✅ לא מציגים “לא התקבלו…” — UX נקי.
                // במקום זה: שורה ניטרלית בלי אשמה/שבירה.
                <Text style={styles.relatedEmptyText}>
                  כרגע אין לנו עוד המלצות מדויקות על בסיס הקורס הזה.
                </Text>
              ) : (
                <View style={styles.relatedList}>
                  {related.slice(0, 6).map((r, idx) => (
                    <RelatedCard
                      key={`${r.course_id}-${idx}`}
                      item={r}
                      index={idx}
                      onPress={() => goToRelated(r.course_id)}
                    />
                  ))}
                </View>
              )}
            </View>
          </Animated.View>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>

      {/* BookCallModal — לכידת ליד */}
      <BookCallModal visible={bookCallVisible} course={courseForModal} onClose={() => setBookCallVisible(false)} />
    </View>
  );
}

/* ── תגית Pill ── */
function Pill({ text, icon }: { text: string; icon?: React.ReactNode }) {
  const t = safeStr(text);
  if (!t) return null;
  return (
    <View style={styles.pill}>
      {icon}
      <Text style={styles.pillText} numberOfLines={1}>
        {t}
      </Text>
    </View>
  );
}

/* ── קטע מידע ── */
function InfoSection({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <View style={styles.infoSection}>
      <View style={styles.infoTitleRow}>
        {icon}
        <Text style={styles.infoTitle}>{title}</Text>
      </View>
      <Text style={styles.infoText}>{text}</Text>
    </View>
  );
}

/* ── כרטיס קורס נוסף עם Stagger ── */
function RelatedCard({
  item,
  index,
  onPress,
}: {
  item: RelatedMini;
  index: number;
  onPress: () => void;
}) {
  const a = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(a, {
      toValue: 1,
      duration: 320,
      delay: 70 + index * 70,
      useNativeDriver: true,
    }).start();
  }, [a, index]);

  const style = useMemo(
    () => ({
      opacity: a,
      transform: [
        {
          translateY: a.interpolate({
            inputRange: [0, 1],
            outputRange: [8, 0],
          }),
        },
      ],
    }),
    [a]
  );

  return (
    <Animated.View style={style}>
      <Pressable onPress={onPress} style={({ pressed }) => [styles.relatedCard, pressed && styles.pressed]}>
        <View style={styles.relatedTopRow}>
          <Text style={styles.relatedTitle} numberOfLines={2}>
            {safeStr(item.title)}
          </Text>
          {item.has_voucher && !!item.voucher_text && (
            <View style={styles.relatedBadge}>
              <Text style={styles.relatedBadgeText} numberOfLines={1}>
                {safeStr(item.voucher_text)}
              </Text>
            </View>
          )}
        </View>

        {!!item.provider && (
          <Text style={styles.relatedProvider} numberOfLines={1}>
            {safeStr(item.provider)}
          </Text>
        )}

        <View style={styles.relatedMetaRow}>
          {!!item.topic && <MiniPill text={item.topic} />}
          {!!item.level && <MiniPill text={item.level} />}
          {!!item.format && <MiniPill text={item.format} />}
          {!!item.duration && <MiniPill text={item.duration} />}
          {!!item.price && <MiniPill text={`₪ ${item.price}`} />}
        </View>
      </Pressable>
    </Animated.View>
  );
}

function MiniPill({ text }: { text: string }) {
  const t = safeStr(text);
  if (!t) return null;
  return (
    <View style={styles.miniPill}>
      <Text style={styles.miniPillText} numberOfLines={1}>
        {t}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  wrap: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 40,
    width: '100%',
    maxWidth: isWeb ? 680 : undefined,
    alignSelf: 'center',
  },

  // Header RTL
  headerTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 15,
    color: theme.colors.text,
    writingDirection: 'rtl',
    textAlign: 'right',
  },

  backBtn: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-end',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.surface,
    marginBottom: 12,
  } as any,
  backText: {
    fontFamily: theme.fonts.bold,
    fontSize: 13,
    color: theme.colors.text,
  },

  center: {
    paddingVertical: 50,
    alignItems: 'center',
    gap: 12,
  },

  card: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.surface,
    borderRadius: 0,
    padding: 18,
    marginBottom: 12,
    ...(isWeb ? ({ boxShadow: '0px 8px 24px rgba(0,0,0,0.09)' } as any) : theme.shadows.md),
  },

  actionsCard: {
    gap: 10,
    marginBottom: 4,
  },

  badge: {
    alignSelf: 'flex-end',
    borderWidth: 1,
    borderColor: theme.colors.primary,
    backgroundColor: 'rgba(200,83,30,0.18)',
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 10,
    marginBottom: 10,
  },
  badgeText: {
    color: theme.colors.primary,
    fontFamily: theme.fonts.bold,
    fontSize: 12,
    textAlign: 'right',
  },

  h1: {
    fontFamily: theme.fonts.bold,
    fontSize: isWeb ? 28 : 22,
    color: theme.colors.text,
    textAlign: 'right',
    lineHeight: isWeb ? 36 : 30,
    marginBottom: 4,
  },
  provider: {
    fontFamily: theme.fonts.medium,
    fontSize: 14,
    color: theme.colors.primary,
    textAlign: 'right',
    marginBottom: 10,
  },

  metaRow: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 12,
  } as any,

  pill: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 4,
    borderWidth: 1,
    borderColor: 'rgba(15,23,42,0.12)',
    backgroundColor: 'rgba(15,23,42,0.04)',
    borderRadius: 999,
    paddingVertical: 5,
    paddingHorizontal: 10,
  } as any,
  pillText: {
    fontFamily: theme.fonts.medium,
    fontSize: 12,
    color: theme.colors.text,
    textAlign: 'right',
  },

  paragraph: {
    fontFamily: theme.fonts.regular,
    fontSize: 14,
    color: theme.colors.text,
    textAlign: 'right',
    lineHeight: 22,
    marginTop: 4,
  },

  infoSection: {
    marginBottom: 14,
  },
  infoTitleRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 5,
    marginBottom: 5,
  } as any,
  infoTitle: {
    fontFamily: theme.fonts.semiBold,
    fontSize: 13,
    color: theme.colors.primary,
    textAlign: 'right',
  },
  infoText: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: theme.colors.text,
    textAlign: 'right',
    lineHeight: 20,
  },

  primaryBtn: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: theme.colors.primary,
    borderRadius: 0,
    paddingVertical: 15,
    paddingHorizontal: 20,
  } as any,
  primaryBtnText: {
    fontFamily: theme.fonts.bold,
    color: '#fff',
    fontSize: 15,
    textAlign: 'center',
  },

  outlineBtn: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: theme.colors.primary,
    borderRadius: 0,
    paddingVertical: 13,
    paddingHorizontal: 20,
    backgroundColor: 'rgba(200,83,30,0.18)',
  } as any,
  outlineBtnText: {
    fontFamily: theme.fonts.bold,
    color: theme.colors.primary,
    fontSize: 14,
    textAlign: 'center',
  },

  muted: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: theme.colors.textLight,
    textAlign: 'right',
    lineHeight: 20,
    marginBottom: 12,
  },

  pressed: {
    opacity: 0.88,
    transform: [{ scale: 0.985 }],
  },

  // קורסים נוספים
  sectionTitleRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  } as any,
  sectionTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 14,
    color: theme.colors.text,
    textAlign: 'right',
  },
  sectionHint: {
    fontFamily: theme.fonts.medium,
    fontSize: 12,
    color: theme.colors.primary,
    textAlign: 'right',
  },

  relatedLoadingRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 6,
  } as any,

  relatedEmptyText: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: theme.colors.textLight,
    textAlign: 'right',
    lineHeight: 20,
  },

  relatedList: {
    gap: 10,
  },

  relatedCard: {
    borderWidth: 1,
    borderColor: 'rgba(15,23,42,0.10)',
    backgroundColor: 'rgba(15,23,42,0.03)',
    borderRadius: 0,
    padding: 12,
  },

  relatedTopRow: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  } as any,

  relatedTitle: {
    flex: 1,
    fontFamily: theme.fonts.semiBold,
    fontSize: 13,
    color: theme.colors.text,
    textAlign: 'right',
    lineHeight: 18,
  },

  relatedProvider: {
    marginTop: 4,
    fontFamily: theme.fonts.medium,
    fontSize: 12,
    color: theme.colors.primary,
    textAlign: 'right',
  },

  relatedMetaRow: {
    marginTop: 8,
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 6,
  } as any,

  relatedBadge: {
    borderWidth: 1,
    borderColor: theme.colors.primary,
    backgroundColor: 'rgba(200,83,30,0.18)',
    borderRadius: 999,
    paddingVertical: 3,
    paddingHorizontal: 8,
    maxWidth: 140,
  },
  relatedBadgeText: {
    fontFamily: theme.fonts.bold,
    fontSize: 11,
    color: theme.colors.primary,
    textAlign: 'right',
  },

  miniPill: {
    borderWidth: 1,
    borderColor: 'rgba(15,23,42,0.12)',
    backgroundColor: 'rgba(255,255,255,0.65)',
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 8,
    maxWidth: 140,
  },
  miniPillText: {
    fontFamily: theme.fonts.medium,
    fontSize: 11,
    color: theme.colors.text,
    textAlign: 'right',
  },
});
